import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreatePackageTemplateDTO,
  LockPartnerProjectPricingDTO,
  SellPackageDTO,
  SellPartnerProjectDTO,
  UpdatePartnerProjectPricingDTO,
  UpdatePackageTemplateDTO,
} from "@/modules/packages/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { classSessionsKeys } from "@/modules/classes/presentation/api/queries";
import { packagesApi } from "./client";
import { SellPartnerScheduleHttpError } from "./sell-partner-schedule-http-error";

// ===== Package Templates =====
export const packageTemplatesKeys = {
  all: ["packages", "templates"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["packages", "templates", "list", q] as const,
  detail: (id: string) => ["packages", "templates", "detail", id] as const,
};

export function usePackageTemplatesQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: packageTemplatesKeys.list(q),
    queryFn: () => packagesApi.templates.list(q),
  });
}

export function usePackageTemplateQuery(id: string) {
  return useQuery({
    queryKey: packageTemplatesKeys.detail(id),
    queryFn: () => packagesApi.templates.get(id),
    enabled: !!id,
  });
}

export function usePackageTemplateUsageStatsQuery(id: string) {
  return useQuery({
    queryKey: ["packages", "templates", "usage-stats", id],
    queryFn: () => packagesApi.templates.getUsageStats(id),
    enabled: !!id,
  });
}

export function useCreatePackageTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePackageTemplateDTO) =>
      packagesApi.templates.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ template ແພັກເກດສຳເລັດ");
      qc.invalidateQueries({ queryKey: packageTemplatesKeys.all });
    },
    onError: () => toast.error("ສ້າງ template ແພັກເກດບໍ່ສຳເລັດ"),
  });
}

export function useUpdatePackageTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePackageTemplateDTO) =>
      packagesApi.templates.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດ template ແພັກເກດສຳເລັດ");
      qc.invalidateQueries({ queryKey: packageTemplatesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: packageTemplatesKeys.all });
    },
    onError: () => toast.error("ອັບເດດ template ແພັກເກດບໍ່ສຳເລັດ"),
  });
}

export function useTogglePackageTemplateStatus(id: string) {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (status: "active" | "inactive") =>
      packagesApi.templates.toggleStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: packageTemplatesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: packageTemplatesKeys.all });
    },
  });

  const run = (status: "active" | "inactive") =>
    new Promise<void>((resolve, reject) => {
      base.mutate(status, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useDeletePackageTemplate() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => packagesApi.templates.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: packageTemplatesKeys.all });
    },
    onError: () => toast.error("ລຶບ template ແພັກເກດບໍ່ສຳເລັດ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// Bulk delete mutation
export function useBulkDeletePackageTemplates() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (ids: string[]) => packagesApi.templates.bulkDelete({ ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: packageTemplatesKeys.all });
    },
  });

  const run = (ids: string[]) =>
    new Promise<{ deletedCount: number }>((resolve, reject) => {
      base.mutate(ids, {
        onSuccess: (result) => resolve(result),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// Bulk toggle status mutation
export function useBulkTogglePackageTemplateStatus() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (input: { ids: string[]; status: "active" | "inactive" }) =>
      packagesApi.templates.bulkToggleStatus(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: packageTemplatesKeys.all });
    },
  });

  const run = (input: { ids: string[]; status: "active" | "inactive" }) =>
    new Promise<{ updatedCount: number }>((resolve, reject) => {
      base.mutate(input, {
        onSuccess: (result) => resolve(result),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ===== Sell Package =====
export const sellPackageKeys = {
  all: ["packages", "sell"] as const,
  checkActivePackage: (studentId: string) =>
    ["packages", "sell", "check-active-package", studentId] as const,
};

export function useCheckActivePackageQuery(studentId: string | null) {
  return useQuery({
    queryKey: sellPackageKeys.checkActivePackage(studentId ?? ""),
    queryFn: () => packagesApi.sell.checkActivePackage(studentId!),
    enabled: !!studentId,
  });
}

export function useSellPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SellPackageDTO) => packagesApi.sell.sellPackage(input),
    onSuccess: () => {
      toast.success("ຂາຍແພັກເກດສຳເລັດ");
      qc.invalidateQueries({ queryKey: sellPackageKeys.all });
    },
    onError: () => toast.error("ຂາຍແພັກເກດບໍ່ສຳເລັດ"),
  });
}

export function useSellPartnerProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SellPartnerProjectDTO) =>
      packagesApi.sell.sellPartnerProject(input),
    onSuccess: () => {
      toast.success("ບັນທຶກໂຄງການສຳເລັດ");
      qc.invalidateQueries({ queryKey: sellPackageKeys.all });
      qc.invalidateQueries({ queryKey: partnerProjectSaleKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.all });
    },
    onError: (error: unknown) => {
      if (error instanceof SellPartnerScheduleHttpError) {
        const preview = error.conflictingDates
          .slice(0, 3)
          .map((c) => `${c.date} ${c.startTime}-${c.endTime}`)
          .join("; ");
        const more =
          error.conflictingDates.length > 3
            ? ` (+${error.conflictingDates.length - 3})`
            : "";
        toast.error(
          preview
            ? `ກຳນົດຕາຕະລາງບໍ່ສຳເລັດ (ທັບກັນ): ${preview}${more}`
            : "ກຳນົດຕາຕະລາງບໍ່ສຳເລັດ (ທັບກັນຄູ ຫ້ອງ ຫຼື ເວລາ)",
        );
        return;
      }
      const errRec =
        error && typeof error === "object"
          ? (error as Record<string, unknown>)
          : null;
      const apiMessage =
        typeof errRec?.message === "string" ? errRec.message : null;
      const apiError =
        typeof errRec?.error === "string" ? errRec.error : null;
      if (
        apiError === "INSTALLMENT_TOTAL_MISMATCH" ||
        apiError === "INSTALLMENTS_REQUIRED"
      ) {
        toast.error(apiMessage ?? "ແຜນງວດຊຳລະບໍ່ຖືກຕ້ອງ");
        return;
      }
      toast.error("ບັນທຶກບໍ່ສຳເລັດ");
    },
  });
}

export const partnerProjectSaleKeys = {
  all: ["packages", "partner-project-sales"] as const,
  pendingLock: (query: OffsetPageQueryDTO) =>
    ["packages", "partner-project-sales", "pending-lock", query] as const,
  detail: (id: string) =>
    ["packages", "partner-project-sales", "detail", id] as const,
};

export function usePendingPartnerProjectSalesQuery(
  query: OffsetPageQueryDTO,
) {
  return useQuery({
    queryKey: partnerProjectSaleKeys.pendingLock(query),
    queryFn: () => packagesApi.partnerProjectSales.listPendingLock(query),
  });
}

export function usePartnerProjectSaleQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: partnerProjectSaleKeys.detail(id),
    queryFn: () => packagesApi.partnerProjectSales.get(id),
    enabled: !!id && enabled,
  });
}

export function useUpdatePartnerProjectPricing(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePartnerProjectPricingDTO) =>
      packagesApi.partnerProjectSales.updatePricing(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດລາຄາສຳເລັດ");
      qc.invalidateQueries({ queryKey: partnerProjectSaleKeys.detail(id) });
    },
    onError: () => toast.error("ອັບເດດລາຄາບໍ່ສຳເລັດ"),
  });
}

export function useLockPartnerProjectPricing(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LockPartnerProjectPricingDTO) =>
      packagesApi.partnerProjectSales.lockPricing(id, input),
    onSuccess: () => {
      toast.success("ກຳນົດລາຄາ ແລະ ເກັບເງິນສຳເລັດ");
      qc.invalidateQueries({ queryKey: partnerProjectSaleKeys.detail(id) });
      qc.invalidateQueries({ queryKey: partnerProjectSaleKeys.all });
      qc.invalidateQueries({ queryKey: sellPackageKeys.all });
    },
    onError: () => toast.error("lock ລາຄາບໍ່ສຳເລັດ"),
  });
}
